/**
 * Reading a submission's `outstandingAtSubmit` (see field.ts).
 *
 * The field tool used to block submit until its own checks were clean. It no
 * longer does — it records what was still outstanding and sends anyway. Two
 * dashboard surfaces have to interpret that record:
 *
 *   - the QA queue, which wants a one-glance "needs work" vs "ready for QA";
 *   - the report detail + QA panel, which want to tell an issue the inspector
 *     already knew about at submit from one QA discovered afterwards.
 *
 * The second needs the device's issue text mapped onto the QA engine's stable
 * rule ids. That correspondence is real, not a guess: the field checks are
 * deliberate mirrors of the QA rules (see the "Parity with the dashboard's
 * pre-upload QA" block in field/src/lib/checklist.ts). But issue text is the
 * one half of the pairing with no compiler behind it, so
 * field/src/lib/outstanding-issues.test.ts feeds every string the device can
 * actually produce through `qaRuleForOutstandingIssue` and fails on any that
 * lands unclassified.
 *
 * A check the field tool runs and QA does not maps to `null` rather than to a
 * wrong rule. The device is allowed to be stricter than QA (never looser), so
 * "the inspector flagged something QA never looks at" is a normal outcome, not
 * a classification failure.
 */
import { MANDATORY_HARDWARE_PHOTOS } from './mandatory-photos.js';
import { PhotoPromptLibrary } from './photo-prompts.js';
const PHOTO_ISSUE_PREFIX = 'Photos: capture required shot - ';
/**
 * Prompt keys that satisfy one outstanding photo line.
 *
 * The field tool names the shot with the prompt's wording, or with the
 * mandatory-hardware label when that shot is not in the prompt library.
 * Both are indexed so a later upload clears the line it actually fills.
 */
let photoIssueKeys;
function promptKeysForPhotoIssueLabel(label) {
    if (!photoIssueKeys) {
        const keys = new Map();
        const add = (name, promptKeys) => {
            const current = keys.get(name) ?? [];
            for (const key of promptKeys) {
                if (!current.includes(key))
                    current.push(key);
            }
            keys.set(name, current);
        };
        for (const prompt of PhotoPromptLibrary)
            add(prompt.shot, [prompt.promptKey]);
        for (const entry of MANDATORY_HARDWARE_PHOTOS)
            add(entry.label, entry.promptKeys);
        photoIssueKeys = keys;
    }
    return photoIssueKeys.get(label) ?? [];
}
/**
 * True when this outstanding line is a missing photo and a current photo
 * already fills that shot. Other outstanding lines are left alone.
 */
export function outstandingPhotoIssueSatisfied(issue, presentPromptKeys) {
    if (!issue.startsWith(PHOTO_ISSUE_PREFIX))
        return false;
    const label = issue.slice(PHOTO_ISSUE_PREFIX.length).replace(/\.$/, '');
    const required = promptKeysForPhotoIssueLabel(label);
    if (required.length === 0)
        return false;
    const present = new Set(presentPromptKeys.map((key) => key.trim()));
    return required.some((key) => present.has(key));
}
/**
 * Prompt keys of the photos on the report now.
 *
 * A manifest is the server copy of that photo, so its key wins over the
 * copy recorded on the submission. A submission photo that has not been
 * uploaded yet still counts. Re-filing therefore clears the old key.
 */
export function promptKeysOnReport(submissionPhotos, manifests = []) {
    const byId = new Map();
    for (const photo of submissionPhotos)
        byId.set(photo.id, photo.promptKey);
    for (const manifest of manifests)
        byId.set(manifest.photoId, manifest.promptKey);
    return [...byId.values()];
}
export function countOutstandingNow(outstandingAtSubmit, presentPromptKeys) {
    if (outstandingAtSubmit === undefined)
        return undefined;
    let remaining = 0;
    for (const issue of outstandingAtSubmit) {
        if (!outstandingPhotoIssueSatisfied(issue, presentPromptKeys))
            remaining += 1;
    }
    return remaining;
}
/**
 * Fold `outstandingAtSubmit` into a readiness verdict.
 *
 * Accepts the list itself (detail views, which hold the whole FieldReport) or
 * just its length (list views, where the summary carries only the count).
 * `undefined` means the field is absent — a pre-Aug-2026 submission.
 */
export function fieldReadiness(outstandingAtSubmit) {
    if (outstandingAtSubmit === undefined) {
        return { state: 'complete', outstandingCount: 0, legacy: true };
    }
    const outstandingCount = typeof outstandingAtSubmit === 'number' ? outstandingAtSubmit : outstandingAtSubmit.length;
    return {
        state: outstandingCount > 0 ? 'needs-work' : 'complete',
        outstandingCount,
        legacy: false,
    };
}
/* ── Mapping issue text onto QA rules ─────────────────────────────────────── */
/**
 * Issue prefixes the field tool uses for report-wide checks. Anything else
 * before the first `": "` is a clause id — no clause id in any of the three
 * clause libraries is one of these words, or contains a colon.
 */
const AREA_RULES = {
    // Mirrors of QA rules.
    'Cover sheet': 'coversheet-complete',
    Consent: 'consent-captured',
    Photos: 'hardware-photos',
    // Field-only: the device asks for these on top of what QA checks.
    'Roof cavity': null,
    Documentation: null,
    Hardware: null,
};
/** Clause-scoped issue wordings, matched against the text after the clause id. */
const CLAUSE_RULES = [
    // Assessment + wording gaps — the `nonconformance-assessments` rule.
    [/^Choose (an|a valid) initial findings assessment/i, 'nonconformance-assessments'],
    [/^initial findings assessment not picked/i, 'nonconformance-assessments'],
    [/^non-compliant but no recommendation wording/i, 'nonconformance-assessments'],
    [/^Add recommendation wording/i, 'nonconformance-assessments'],
    // Clause-level photo evidence — the `checklist-photos` rule.
    [/^A photo is required for /i, 'checklist-photos'],
    [/^Capture required photo: /i, 'checklist-photos'],
    // Field-only: QA has no rule for "the clause was never answered at all".
    [/^response missing/i, null],
];
/**
 * Which QA rule an outstanding entry mirrors.
 *
 * Returns `null` for a field-only check (one QA does not run), and
 * `undefined` when the text matches nothing known — which means the device's
 * wording has drifted from this map, not that the issue is unimportant.
 * Callers should surface an unclassified issue rather than drop it.
 */
export function qaRuleForOutstandingIssue(issue) {
    const separator = issue.indexOf(': ');
    if (separator === -1)
        return undefined;
    const prefix = issue.slice(0, separator).trim();
    const rest = issue.slice(separator + 2).trim();
    if (prefix in AREA_RULES)
        return AREA_RULES[prefix];
    // Otherwise the prefix is a clause id and the rule is decided by the gap.
    for (const [test, ruleId] of CLAUSE_RULES) {
        if (test.test(rest))
            return ruleId;
    }
    return undefined;
}
/**
 * Cross-reference `outstandingAtSubmit` against a QA report.
 *
 * Pass an empty `qaResults` when QA has not run — every entry then reads as
 * `not-qa-checked` and no origins are produced, which is the truthful answer
 * rather than an optimistic one.
 */
export function reconcileOutstandingWithQa(outstandingAtSubmit, qaResults, 
/**
 * Prompt keys of the photos on the report now, including shots a reviewer
 * added after submit. A missing-photo line those keys fill is resolved
 * even when the hardware-photos rule is still failing for a different shot.
 */
presentPromptKeys) {
    const readiness = fieldReadiness(outstandingAtSubmit);
    const statusByRuleId = new Map(qaResults.map((r) => [r.ruleId, r.status]));
    const entries = (outstandingAtSubmit ?? []).map((issue) => {
        const ruleId = qaRuleForOutstandingIssue(issue);
        if (presentPromptKeys && outstandingPhotoIssueSatisfied(issue, presentPromptKeys)) {
            return {
                issue,
                ruleId: typeof ruleId === 'string' ? ruleId : null,
                disposition: 'resolved',
            };
        }
        if (ruleId === undefined)
            return { issue, ruleId: null, disposition: 'unclassified' };
        if (ruleId === null)
            return { issue, ruleId: null, disposition: 'not-qa-checked' };
        const status = statusByRuleId.get(ruleId);
        if (status === undefined)
            return { issue, ruleId, disposition: 'not-qa-checked' };
        return { issue, ruleId, disposition: status === 'pass' ? 'resolved' : 'open' };
    });
    const flagged = new Set(entries.filter((e) => e.ruleId !== null).map((e) => e.ruleId));
    const originByRuleId = new Map();
    for (const result of qaResults) {
        if (result.status === 'pass')
            continue;
        originByRuleId.set(result.ruleId, readiness.legacy ? 'unrecorded' : flagged.has(result.ruleId) ? 'inspector-known' : 'qa-found');
    }
    return { readiness, entries, originByRuleId };
}
/** The inspector's own wording for a QA rule, for showing under the finding. */
export function inspectorNotesForRule(entries, ruleId) {
    return entries.filter((e) => e.ruleId === ruleId).map((e) => e.issue);
}
//# sourceMappingURL=field-readiness.js.map